import { Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useAddContacts, useSyncContacts } from '@/api/hooks/use-audiences'
import { AudienceManualAdd } from '@/pages/audiences/audience-manual-add'

interface AudienceImportTabProps {
  audienceId: string
}

type ImportMode = 'add' | 'sync'

interface ImportResult {
  added: number
  updated: number
  cleaned?: number
  skipped?: number
  skipped_emails?: string[]
}

export function AudienceImportTab({ audienceId }: AudienceImportTabProps): React.JSX.Element {
  const [importMode, setImportMode] = useState<ImportMode>('add')
  const [isDragging, setIsDragging] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addContacts = useAddContacts()
  const syncContacts = useSyncContacts()
  const isPending = addContacts.isPending || syncContacts.isPending

  function parseCsv(text: string): Array<{ email: string; metadata?: Record<string, unknown> }> {
    const lines = text.trim().split('\n')
    if (lines.length < 2) return []

    const headerLine = lines[0]
    if (!headerLine) return []
    const headers = headerLine.split(',').map((h) => h.trim().toLowerCase())
    const emailIndex = headers.indexOf('email')
    if (emailIndex === -1) return []

    const metadataKeys = headers.filter((h) => h !== 'email')

    const parsed: Array<{ email: string; metadata?: Record<string, unknown> }> = []

    for (const line of lines.slice(1)) {
      const values = line.split(',').map((v) => v.trim())
      const email = values[emailIndex]
      if (!email) continue

      const metadata: Record<string, unknown> = {}
      for (const key of metadataKeys) {
        const idx = headers.indexOf(key)
        if (idx !== -1 && values[idx]) {
          metadata[key] = values[idx]
        }
      }
      parsed.push({ email, metadata: Object.keys(metadata).length > 0 ? metadata : undefined })
    }

    return parsed
  }

  function handleFile(file: File): void {
    setResult(null)
    setError(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const contacts = parseCsv(text)

      if (contacts.length === 0) {
        setError('No valid contacts found. Make sure the CSV has an "email" column header.')
        return
      }

      if (importMode === 'sync') {
        syncContacts.mutate(
          { audience_id: audienceId, contacts },
          {
            onSuccess: (res) =>
              setResult({ added: res.added, updated: res.updated, cleaned: res.cleaned }),
            onError: () => setError('Failed to sync contacts. Please try again.'),
          },
        )
      } else {
        addContacts.mutate(
          { audience_id: audienceId, contacts },
          {
            onSuccess: (res) =>
              setResult({
                added: res.added,
                updated: res.updated,
                skipped: res.skipped,
                skipped_emails: res.skipped_emails,
              }),
            onError: () => setError('Failed to add contacts. Please try again.'),
          },
        )
      }
    }
    reader.readAsText(file)
  }

  function handleDrop(e: React.DragEvent): void {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file?.name.endsWith('.csv')) {
      handleFile(file)
    } else {
      setError('Please upload a .csv file.')
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  return (
    <div className="space-y-6">
      {/* Import mode toggle */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-text-primary">Import mode:</span>
        <div className="flex rounded-lg border border-surface-3">
          <button
            type="button"
            onClick={() => setImportMode('add')}
            className={`px-4 py-1.5 text-sm font-medium ${
              importMode === 'add'
                ? 'bg-brand-500 text-white'
                : 'text-text-secondary hover:bg-surface-1'
            } rounded-l-lg`}
          >
            Add new contacts
          </button>
          <button
            type="button"
            onClick={() => setImportMode('sync')}
            className={`px-4 py-1.5 text-sm font-medium ${
              importMode === 'sync'
                ? 'bg-brand-500 text-white'
                : 'text-text-secondary hover:bg-surface-1'
            } rounded-r-lg`}
          >
            Full sync (replace all)
          </button>
        </div>
      </div>

      {importMode === 'sync' && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Full sync will mark contacts not in the uploaded file as &quot;cleaned&quot;. Only use
          this to completely replace the audience with a fresh list.
        </div>
      )}

      {/* CSV format example */}
      <div className="rounded-lg border border-surface-3 bg-surface-1 p-4">
        <p className="mb-2 text-sm font-medium text-text-primary">CSV format</p>
        <pre className="text-xs text-text-secondary">
          {`email,name,plan
alice@example.com,Alice,pro
bob@example.com,Bob,free
carol@example.com,Carol,enterprise`}
        </pre>
      </div>

      {/* Drop zone */}
      {/* biome-ignore lint/a11y/useSemanticElements: drag-and-drop zone with complex inner layout */}
      <div
        role="button"
        tabIndex={0}
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            fileInputRef.current?.click()
          }
        }}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-colors ${
          isDragging
            ? 'border-brand-500 bg-brand-50'
            : 'border-surface-3 hover:border-brand-300 hover:bg-surface-1'
        }`}
      >
        <Upload className="mb-3 h-8 w-8 text-text-tertiary" />
        <p className="text-sm font-medium text-text-primary">
          {isPending ? 'Uploading...' : 'Drop a CSV file here or click to upload'}
        </p>
        <p className="mt-1 text-xs text-text-tertiary">Up to 1,000 contacts per upload</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInput}
          className="hidden"
        />
      </div>

      {/* Result/error messages */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <p className="font-medium">Import complete</p>
          <ul className="mt-1 list-inside list-disc">
            <li>{result.added} added</li>
            <li>{result.updated} updated</li>
            {result.cleaned !== undefined && <li>{result.cleaned} cleaned</li>}
            {result.skipped !== undefined && result.skipped > 0 && (
              <li>{result.skipped} skipped (suppressed)</li>
            )}
          </ul>
        </div>
      )}

      {/* Manual add section */}
      <AudienceManualAdd audienceId={audienceId} />
    </div>
  )
}
